<template>
  <div>
    <div
      v-if="isLoading"
      class="loading"
    >
      <h3>{{ trans('core.loading') }}</h3>
      <div class="progress">
        <div
          class="progress-bar progress-bar-striped"
          role="progressbar"
          :style="{ width: loadingProgress * 100 + '%' }"
        />
      </div>
    </div>

    <div
      v-else
      class="attendance-goal-overview"
    >
      <AgGridTable
        class="ag-grid"
        :grid-options="gridOptions"
        :column-defs="columns"
        :row-data="rows"
        :paginate="false"
        :fit-cols="false"
      />
    </div>

    <!-- EventCluster details -->
    <Modal
      v-if="selectedCluster != null"
      :open="selectedCluster != null"
      :title="selectedCluster.label"
      @close="selectedCluster = null"
    >
      <div v-if="isLoadingClusterDetails">
        <div style="text-align:center; transition: height 1s ease;">
          <i
            class="fa fa-spinner fa-pulse fa-2x fa-fw"
            aria-hidden="true"
          />
          <span class="sr-only">{{ trans('core.loading') }}</span>
        </div>
      </div>
      <div
        v-else
        v-safe-html="clusterDetails"
      />
    </Modal>

    <!-- StudentCluster details -->
    <Modal
      v-if="selectedStudentCluster != null"
      :open="selectedStudentCluster != null"
      :title="''"
      @close="selectedStudentCluster = null"
    >
      <div v-if="isLoadingStudentClusterDetails">
        <div style="text-align:center; transition: height 1s ease;">
          <i
            class="fa fa-spinner fa-pulse fa-2x fa-fw"
            aria-hidden="true"
          />
          <span class="sr-only">{{ trans('core.loading') }}</span>
        </div>
      </div>
      <div
        v-else
        v-safe-html="studentClusterDetails"
      />
    </Modal>
  </div>
</template>

<script>
import AgGridTable from '@/employees/components/AgGridTable'
import Modal from '@/employees/AttendanceList/components/Modal'
import AttendanceGoalMatrixHeader from '@/employees/components/AttendanceGoal/AttendanceGoalMatrixHeader'
import { mapState } from 'vuex'
import { COLORS, MATRIX_ATTENDANCE_GOALS_CHUNK_SIZE, STUDENT_SERIE_STATUS } from '@/constants'
import { stringSorter, studentSortColumns } from '@/helpers'
import moment from 'moment'

export default {
  name: 'AttendanceGoalMatrixOverview',
  components: { Modal, AgGridTable, AttendanceGoalMatrixHeader },
  props: {
    eventSerieId: {
      required: true,
      type: String,
    },
  },

  data () {
    return {
      isLoading: true,
      loadingProgress: 0,

      gridOptions: {
        defaultColDef: {
          sortable: false,
          resizable: false,
        },
        headerHeight: 60,
        rowBuffer: 25,
        rowHeight: 60,
        suppressCellFocus: true,
        suppressHeaderFocus: true,
        enableBrowserTooltips: true,
        pagination: false,
        onColumnHeaderClicked: this.onHeaderClicked,
        suppressPropertyNamesCheck: true,
      },

      selectedCluster: null,
      isLoadingClusterDetails: false,
      clusterDetails: null,

      selectedStudentCluster: null,
      isLoadingStudentClusterDetails: false,
      studentClusterDetails: null,
    }
  },

  computed: {
    ...mapState({
      clusters (state) {
        return state.eventSeries.eventClusters
      },

      studentSeries (state) {
        // Student series keyed by the student_id
        return Object.values(state.eventSeries.studentSeries).reduce((acc, studentSerie) => {
          acc[studentSerie.student] = studentSerie
          return acc
        }, {})
      },

      studentClusters (state) {
        // Student clusters keyed by the student_id and cluster_id
        return Object.values(state.eventSeries.studentClusters).reduce((acc, studentCluster) => {
          if (!acc.hasOwnProperty(studentCluster.student_id)) {
            acc[studentCluster.student_id] = {}
          }

          acc[studentCluster.student_id][studentCluster.event_cluster_id] = studentCluster

          return acc
        }, {})
      },

      students (state) {
        return Object.values(state.students.students)
      },

      groups (state) {
        return state.groups.groups
      },
    }),

    columns () {
      const clusterColumns = Object.values(this.clusters).sort(stringSorter('cluster_parameter'))
        .map(cluster => ({
          headerName: cluster.label,
          field: `${cluster.id}`,
          cellRenderer: this.renderClusterStatusCell,
          headerComponent: 'AttendanceGoalMatrixHeader',
          headerComponentParams: { name: cluster.label },
          cellClass: 'status',
          getQuickFilterText: () => null,
          width: 100,
          cellStyle: this.cellStyle,
          onClick: () => {
            this.selectedCluster = cluster
          },
        }))

      return [
        {
          headerName: Lang.get('core.name'),
          field: 'student',
          pinned: 'left',
          cellRenderer: this.renderStudentCell,
          valueFormatter: params => params.value.name,
          getQuickFilterText: (params) => {
            return params.value
          },
          cellStyle: this.cellStyle,
        },
        {
          headerName: `${Lang.get('core.goal')} ${Lang.get('core.achieved').toLowerCase()}`,
          field: 'serieStatus',
          pinned: 'left',
          cellRenderer: this.renderSerieStatusCell,
          cellClass: 'status',
          getQuickFilterText: () => null,
          cellStyle: this.cellStyle,
          width: 100,
        },
        ...clusterColumns,
      ]
    },

    rows () {
      return this.sortedStudents.map(student => {
        return {
          student,
          ...this.studentClusters[student.id],
          serieStatus: this.studentSeries[student.id],
        }
      })
    },

    sortedStudents () {
      return _.orderBy(this.students, studentSortColumns())
    },

    cellStyle () {
      return { 'font-size': '14px', padding: '5px', 'font-family': 'Roboto' }
    },
  },

  watch: {
    selectedStudentCluster: function (studentCluster) {
      if (studentCluster != null) {
        this.loadStudentClusterDetails(studentCluster)
      }
    },
    selectedCluster: function (cluster) {
      if (cluster != null) {
        this.loadClusterDetails(cluster)
      }
    },
  },

  created () {
    this.loadItems()
  },

  methods: {
    async loadItems () {
      this.isLoading = true
      this.loadingProgress = 0
      const calls = []

      // get all clusters for this attendance goal
      await this.$store.dispatch('getAttendanceGoalMatrixClusters', {
        eventSerieId: this.eventSerieId,
      })

      // get all the students for this attendance goal
      calls.push(this.$store.dispatch('getAttendanceGoalMatrixStudents', {
        eventSerieId: this.eventSerieId,
      }))

      this.loadingProgress = 0.25

      const clusterIds = Object.keys(this.clusters)
      const chunkedClusterIds = _.chunk(clusterIds, MATRIX_ATTENDANCE_GOALS_CHUNK_SIZE)
      const singleStepProgression = 0.75 / chunkedClusterIds.length

      // Do a call for each chunk and increment the loading progress
      for (const chunk of chunkedClusterIds) {
        calls.push(this.$store.dispatch('getAttendanceGoalMatrixDetails', {
          eventSerieId: this.eventSerieId,
          queryParams: {
            filter: {
              id: chunk,
            },
          },
        }).then(() => {
          this.loadingProgress += singleStepProgression
        }))
      }

      await Promise.all(calls)
      this.loadingProgress = 1
      this.isLoading = false
    },

    loadClusterDetails (cluster) {
      this.isLoadingClusterDetails = true
      this.clusterDetails = null

      this.$store.dispatch('getEventClusterDetails', {
        eventSerieId: this.eventSerieId,
        eventClusterId: cluster.id,
      }).then((data) => {
        this.clusterDetails = data.data
      }).catch(() => {
        this.clusterDetails = Lang.get('errors.message.generalerror')
      }).finally(() => {
        this.isLoadingClusterDetails = false
      })
    },

    loadStudentClusterDetails (studentCluster) {
      this.isLoadingStudentClusterDetails = true
      this.studentClusterDetails = null

      this.$store.dispatch('getStudentClusterDetails', {
        eventSerieId: this.eventSerieId,
        eventClusterId: studentCluster.event_cluster_id,
        studentId: studentCluster.student_id,
      }).then((data) => {
        this.studentClusterDetails = data.data
      }).catch(() => {
        this.studentClusterDetails = Lang.get('errors.message.generalerror')
      }).finally(() => {
        this.isLoadingStudentClusterDetails = false
      })
    },

    renderStudentCell (cellData) {
      const student = cellData.value
      const groupNames = student.groups
        .map(groupId => this.groups[groupId].name)
        .filter((value, index, array) => {
          // unique group names
          return array.indexOf(value) === index
        })
        .join(', ')

      const div = document.createElement('div')
      div.innerHTML = `<span class="studentbutton" style="cursor:pointer">
        <strong style="color: #b71c1c">${_.escape(student.name)}</strong>` +
        (student.stopDate != null ? `<i class="fa fa-ban" title="${Lang.get('core.stopped_per', { date: student.stopDate })}" data-toggle="tooltip" data-placement="top" style="padding-left: 5px"></i>` : '') +
        (groupNames.length < 1 ? '' : `<br><small>${_.escape(groupNames)}</small></span>`)

      if (student.link) {
        const button = div.querySelectorAll('.studentbutton')[0]

        button.addEventListener('click', function () {
          window.open(student.link)
        })
      }

      return div
    },

    renderSerieStatusCell (cellData) {
      const studentSerie = cellData.value

      const div = document.createElement('div')

      if (studentSerie.status === STUDENT_SERIE_STATUS.finished || studentSerie.finished_at != null) {
        // goal finished
        div.innerHTML = `<span class="fa fa-check" data-toggle="tooltip" title="${Lang.get('series.status.finished')}" style="color: ${COLORS.attended}"></span>`
      } else if (studentSerie.status === STUDENT_SERIE_STATUS.pending) {
        // pending
        div.innerHTML = `<span class="fa fa-minus" data-toggle="tooltip" title="${Lang.get('series.status.pending')}" style="color: ${COLORS.defaultGrey}"></span>`
      } else if (studentSerie.status === STUDENT_SERIE_STATUS.not_relevant) {
        // not relevant, render nothing
      } else {
        // failed
        div.innerHTML = `<span class="fa fa-remove" data-toggle="tooltip" title="${Lang.get('series.status.failed')}" style="color: ${COLORS.absent}"></span>`
      }

      return div
    },

    renderClusterStatusCell (cellData) {
      const studentCluster = cellData.value ?? null
      const cluster = studentCluster ? this.clusters[studentCluster.event_cluster_id] : this.clusters[cellData.column.colId]

      let color = null
      let icon = null
      if (studentCluster != null && studentCluster.finished_at != null) {
        // finished
        color = COLORS.attended
        icon = 'fa fa-check'
      } else {
        if (cluster != null && moment(cluster.last_event) > moment()) {
          // not finished and has future events: pending
          color = COLORS.defaultGrey
          icon = 'fa fa-minus'
        } else {
          // else failed
          color = COLORS.absent
          icon = 'fa fa-remove'
        }
      }

      const div = document.createElement('div')
      const anchor = document.createElement('a')

      anchor.onclick = () => {
        // if the student has no status for the cluster, we still want it clickable to open the modal
        this.selectedStudentCluster = {
          student_id: studentCluster?.student_id ?? cellData.data.student.id,
          event_cluster_id: studentCluster?.event_cluster_id ?? cellData.column.colId,
        }
      }

      anchor.style.color = color
      anchor.style.cursor = 'pointer'
      anchor.innerHTML = `<span alt="title" class="${icon}" style="color: ${color}"></span>`

      div.appendChild(anchor)
      return div
    },

    getNameFilter (cellData) {
      return cellData.value.name
    },
  },
}
</script>

<style lang="scss">
.attendance-goal-overview {
  flex: 0 1 auto;
  display: flex;
  flex-direction: column;
  height: inherit;

  .ag-grid {
    display: flex;
    flex: 1 1 auto;
    overflow-y: auto;
    overflow-x: auto;
    height: inherit;
  }

  .ag-row .ag-cell {
    display: flex;
    align-items: center;

    &.status {

      justify-content: center; /* align horizontal */

    }
  }

  .ag-row {
    border-top: 1px solid #dee2e6;
  }

  .ag-cell {
    line-height: 20px !important;
  }

  .ag-horizontal-left-spacer, .ag-horizontal-right-spacer {
    visibility: hidden;
  }
}

/* badge counter */
.fa-icon-badge:after {
  content: attr(data-count);
  font-family: Roboto, Helvetica, Arial, sans-serif;
  position: absolute;
  background: rgba(0, 0, 0, 1);
  height: 1.3rem;
  top: -0.2rem;
  right: -0.4rem;
  width: 1.3rem;
  padding-right: 0.1rem;
  padding-left: 0.0rem;
  text-align: center;
  line-height: 1.1rem;
  font-size: 1.1rem;
  border-radius: 50%;
  color: white;
  border: 1px solid rgba(0, 0, 0, 1);
}

.loading {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  flex: 1 0 auto;

  .progress {
    width: 75%;
  }
}
</style>
